import { OTLayout } from "@/components/layout/ot-layout";
import { FlaskConical, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { useState } from "react";

const ACADEMIC_ROUTES = [
  {
    title: "NIHR Clinical Research Internship",
    description: "The National Institute for Health and Care Research (NIHR) offers AHP clinical research internships, enabling OTs to develop research skills while maintaining clinical practice.",
    details: "Internships are typically 6–12 months (part-time or full-time) and are designed for allied health professionals wanting to develop research competencies. Rheumatology OTs with a clinical question are well placed to apply. Internships lead to involvement in funded research studies and help build a track record for fellowship applications.",
    link: "https://www.nihr.ac.uk/",
    linkLabel: "NIHR AHP Research",
  },
  {
    title: "MSc by Research or Professional Doctorate",
    description: "A structured postgraduate research qualification in OT, MSK health, or a related field, providing systematic research training and a substantial independent research project.",
    details: "A PhD or professional doctorate (DProf) is the gold standard for academic OT. Many rheumatology OTs undertake an MSc first to develop research skills and test a research area before committing to a PhD. Topics relevant to rheumatology OT include joint protection, fatigue management, occupational performance, and technology-enabled self-management.",
    link: "https://www.rcot.co.uk/research",
    linkLabel: "RCOT Research",
  },
  {
    title: "Clinical Academic Fellowship",
    description: "A combined clinical and academic role, typically based at a university with a linked NHS trust. Allows sustained contribution to both clinical practice and research.",
    details: "Clinical academic fellowships are often funded by NIHR or HEE and are designed for mid-career clinicians. In rheumatology OT, fellowships may be embedded within academic rheumatology departments (e.g. UCL, Leeds, Oxford) or occupational therapy schools with rheumatology research programmes.",
    link: "https://www.nihr.ac.uk/career-development/awards-and-internships/",
    linkLabel: "NIHR Career Development Awards",
  },
  {
    title: "RCOT Research Grants & Bursaries",
    description: "RCOT offers several small grant and bursary schemes to support OT research, including the Foundation for Occupational Therapy and RCOT small grants.",
    details: "RCOT small grants are typically £500–£5,000 and are suitable for pilot projects, systematic reviews, or conference dissemination. The Foundation for Occupational Therapy offers larger awards. Grants are competitive — a well-defined research question relevant to rheumatology OT practice and a clear methodology will strengthen applications.",
    link: "https://www.rcot.co.uk/research/grants-and-funding",
    linkLabel: "RCOT Grants",
  },
  {
    title: "Arthritis Research UK / Versus Arthritis Funding",
    description: "Versus Arthritis (formerly Arthritis Research UK) funds MSK research including OT-relevant studies. Partnership applications with rheumatology departments are well-regarded.",
    details: "OT researchers can apply independently or as part of multi-disciplinary teams. Patient-public involvement (PPI) is now expected in all applications. Joint applications between OTs and rheumatology consultants or academics are particularly competitive. Relevant OT topics include hand therapy, vocational rehabilitation, and self-management programme evaluation.",
    link: "https://www.versusarthritis.org/research/",
    linkLabel: "Versus Arthritis Research",
  },
];

const BALANCING_TIPS = [
  "Negotiate a protected research day per week in your job plan before starting a PhD or clinical fellowship.",
  "Choose a research question that is directly relevant to your clinical practice — this makes both easier.",
  "Engage with your university's OT research group or rheumatology department early to find potential supervisors.",
  "Start with small projects: service evaluations, audit, or systematic reviews before applying for funded research.",
  "Use your RCOT SS Rheumatology network to find OT research collaborators across the UK.",
  "Apply for conference bursaries (RCOT, BSR, EULAR) to present your work and build your academic profile.",
];

export default function OtAcademic() {
  const [openRoute, setOpenRoute] = useState<number | null>(null);

  return (
    <OTLayout title="Academic OT Routes">
      <div className="space-y-6">

        <div className="bg-gradient-to-br from-violet-700 to-violet-900 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <FlaskConical className="h-7 w-7 text-violet-300" />
            <div>
              <h2 className="text-xl font-bold">Academic OT Career Routes</h2>
              <p className="text-violet-200 text-sm">Clinical academic pathways for rheumatology occupational therapists</p>
            </div>
          </div>
          <p className="text-violet-100 text-sm leading-relaxed">
            Academic occupational therapy combines clinical practice with research, generating evidence that improves OT outcomes. Rheumatology OTs are well-placed to lead research in joint protection, fatigue management, assistive technology, and occupational performance. This page outlines the main routes into academic OT practice.
          </p>
        </div>

        <div className="space-y-3">
          {ACADEMIC_ROUTES.map((r, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <button
                onClick={() => setOpenRoute(openRoute === i ? null : i)}
                className="flex items-center justify-between w-full px-6 py-4 text-left hover:bg-slate-50 transition-colors"
              >
                <div>
                  <p className="text-sm font-semibold text-slate-800">{r.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{r.description}</p>
                </div>
                {openRoute === i ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0 ml-4" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0 ml-4" />}
              </button>
              {openRoute === i && (
                <div className="px-6 pb-5 space-y-3 border-t border-slate-100 pt-3">
                  <p className="text-sm text-slate-600 leading-relaxed">{r.details}</p>
                  <a
                    href={r.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-violet-700 hover:text-violet-800 font-medium"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {r.linkLabel}
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">Balancing Clinical and Academic Work</h3>
          <ul className="space-y-2">
            {BALANCING_TIPS.map((tip, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-violet-400 shrink-0" />
                {tip}
              </li>
            ))}
          </ul>
        </div>

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>Research in rheumatology OT:</strong> UK rheumatology OT research is growing but remains a relatively small field. Collaborating with rheumatology consultants, nurses, and physiotherapists in multi-disciplinary research bids significantly strengthens grant applications and broadens the impact of your findings.
        </div>
      </div>
    </OTLayout>
  );
}
